/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import Hammer from 'hammerjs';
import { WheelListener } from './WheelListener';
/**
 * Active gesture state
 */
var Gesture;
(function (Gesture) {
    Gesture[Gesture["None"] = 0] = "None";
    Gesture[Gesture["Pan"] = 1] = "Pan";
    Gesture[Gesture["Pinch"] = 2] = "Pinch";
})(Gesture || (Gesture = {}));
const PINCH_WHEEL_FACTOR = 8900;
const CURSOR_STYLE_DEFAULT = 'default';
const CURSOR_STYLE_GESTURE = 'grabbing';
const setCursorStyle = (domElement, cursor = CURSOR_STYLE_DEFAULT) => {
    domElement.style.cursor = cursor;
};
const registerListeners = (domElement, hammertime, stage) => {
    let lastX = 0;
    let lastY = 0;
    let lastScale = 0;
    let lastAngle = 0;
    let curGesture = Gesture.None;
    let nextPanMouseBtn = 0;
    let curPanMouseBtn = 0;
    const panStart = (gesture) => {
        const { x, y } = gesture.center;
        lastX = x;
        lastY = y;
    };
    const panMove = (gesture) => {
        const { x, y } = gesture.center;
        const movementX = x - lastX;
        const movementY = y - lastY;
        lastX = x;
        lastY = y;
        return {
            movementX,
            movementY,
            mouseBtn: curPanMouseBtn,
        };
    };
    const panDispatch = (detail) => {
        domElement.dispatchEvent(new CustomEvent('pan', { detail }));
    };
    hammertime.on('panstart', (gesture) => {
        panStart(gesture);
        if (curGesture === Gesture.None) {
            curGesture = Gesture.Pan;
            curPanMouseBtn = nextPanMouseBtn;
            setCursorStyle(domElement, CURSOR_STYLE_GESTURE);
        }
    });
    hammertime.on('panmove', (gesture) => {
        const movement = panMove(gesture);
        if (curGesture === Gesture.Pan) {
            panDispatch(Object.assign({}, movement, { gesture, isPinch: false }));
        }
    });
    hammertime.on('panend', () => {
        if (curGesture === Gesture.Pan) {
            curGesture = Gesture.None;
            nextPanMouseBtn = 0;
            setCursorStyle(domElement);
        }
    });
    hammertime.on('pinchstart', (gesture) => {
        lastScale = gesture.scale;
        lastAngle = gesture.rotation;
        panStart(gesture);
        if (curGesture === Gesture.None) {
            curGesture = Gesture.Pinch;
            curPanMouseBtn = -1;
            setCursorStyle(domElement, CURSOR_STYLE_GESTURE);
        }
    });
    hammertime.on('pinch', (gesture) => {
        if (curGesture === Gesture.Pinch) {
            const { scale, rotation } = gesture;
            const pinch = {
                detail: {
                    scale: (scale - lastScale) * PINCH_WHEEL_FACTOR,
                    angle: (rotation - lastAngle),
                    isWheel: false,
                },
            };
            domElement.dispatchEvent(new CustomEvent('pinch', pinch));
            lastScale = scale;
            lastAngle = rotation;
            panDispatch(Object.assign({}, panMove(gesture), { gesture, isPinch: true }));
        }
    });
    new WheelListener().onWheel(domElement, (event) => {
        domElement.dispatchEvent(new CustomEvent('pinch', {
            detail: {
                scale: -event.deltaY,
                angle: 0,
                isWheel: true,
            },
        }));
    }, {
        passive: true,
    });
    hammertime.on('pinchend', () => {
        if (curGesture === Gesture.Pinch) {
            curGesture = Gesture.None;
            nextPanMouseBtn = 0;
            setCursorStyle(domElement);
        }
    });
    const selectViewport = (event) => {
        if (curGesture === Gesture.None) {
            if ('touches' in event) {
                const { clientX, clientY } = event.touches[0];
                stage.selectViewport(clientX, clientY);
            }
            else if ('clientX' in event && 'clientY' in event) {
                stage.selectViewport(event.clientX, event.clientY);
            }
        }
    };
    hammertime.on('hammer.input', ({ srcEvent }) => selectViewport(srcEvent));
    domElement.addEventListener('wheel', selectViewport, { passive: true });
    domElement.addEventListener('pointerdown', (pointerDownEv) => {
        if (pointerDownEv.pointerType === 'mouse') {
            if (pointerDownEv.button === 1 || pointerDownEv.button === 2) {
                let artificialEv;
                try {
                    artificialEv = new PointerEvent('pointerdown', {
                        // PointerEvent
                        pointerId: pointerDownEv.pointerId,
                        width: pointerDownEv.width,
                        height: pointerDownEv.height,
                        pressure: pointerDownEv.pressure,
                        tangentialPressure: pointerDownEv.tangentialPressure,
                        tiltX: pointerDownEv.tiltX,
                        tiltY: pointerDownEv.tiltY,
                        twist: pointerDownEv.twist,
                        pointerType: pointerDownEv.pointerType,
                        isPrimary: pointerDownEv.isPrimary,
                        // MouseEvents
                        screenX: pointerDownEv.screenX,
                        screenY: pointerDownEv.screenY,
                        clientX: pointerDownEv.clientX,
                        clientY: pointerDownEv.clientY,
                        ctrlKey: pointerDownEv.ctrlKey,
                        shiftKey: pointerDownEv.shiftKey,
                        altKey: pointerDownEv.altKey,
                        metaKey: pointerDownEv.metaKey,
                        button: 0,
                        buttons: 1,
                        relatedTarget: pointerDownEv.relatedTarget,
                        // UIEvent
                        detail: pointerDownEv.detail,
                        view: pointerDownEv.view,
                        // Event
                        bubbles: pointerDownEv.bubbles,
                        cancelable: pointerDownEv.cancelBubble,
                        composed: pointerDownEv.composed,
                    });
                }
                catch (_err) { // .. and now the IE 11 variant:
                    artificialEv = document.createEvent('PointerEvent');
                    // @ts-ignore
                    artificialEv.initPointerEvent('pointerdown', pointerDownEv.bubbles, pointerDownEv.cancelBubble, pointerDownEv.view, pointerDownEv.detail, pointerDownEv.screenX, pointerDownEv.screenY, pointerDownEv.clientX, pointerDownEv.clientY, pointerDownEv.ctrlKey, pointerDownEv.altKey, pointerDownEv.shiftKey, pointerDownEv.metaKey, 0, pointerDownEv.relatedTarget, pointerDownEv.clientX, pointerDownEv.clientY, pointerDownEv.width, pointerDownEv.height, pointerDownEv.pressure, pointerDownEv.tangentialPressure, pointerDownEv.tiltX, pointerDownEv.tiltY, pointerDownEv.pointerId, pointerDownEv.pointerType, new Date().getTime(), pointerDownEv.isPrimary);
                }
                nextPanMouseBtn = pointerDownEv.button;
                domElement.dispatchEvent(artificialEv);
            }
        }
    });
};
const preventDefaults = (domElement) => {
    const preventDefault = (event) => {
        event.preventDefault();
    };
    domElement.addEventListener('contextmenu', preventDefault, { passive: false });
};
/**
 * The primary touch and pointer events controller.
 *
 * Subscribes to mouse/touch/wheel/.. input events
 * and produces synthetic `pan` and `pinch` events.
 *
 * Under the hood the hammer.js library is used for subscribing to gesture events.
 *
 * You shouldn't create instances by yourself - this is normally done inside the [[Stage]] once.
 *
 * @see [[PanEvent]]
 * @see [[PinchEvent]]
 */
export class InputController {
    constructor(stage) {
        this.stage = stage;
        if (1) {
            const el = stage.getRenderer().domElement.parentElement;
            const hammertime = new Hammer(el, {
                domEvents: false,
            });
            hammertime.get('pinch').set({ enable: true });
            hammertime.get('pan').set({ enable: true, direction: Hammer.DIRECTION_ALL, threshold: 0 });
            const { domElement } = this.stage.getRenderer();
            registerListeners(domElement, hammertime, stage);
            preventDefaults(domElement);
            setCursorStyle(domElement);
            // uncomment to view all hammer.js events in your console:
            // tslint:disable-next-line:no-any
            // hammertime.on('hammer.input', (ev: any) => {
            // tslint:disable-next-line:no-console
            // console.debug('hammer.input', ev);
            // });
        }
    }
}
//# sourceMappingURL=InputController.js.map